import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, Avatar, Dialog, DialogContent, IconButton, useMediaQuery, useTheme, CircularProgress, Alert } from '@mui/material';
import { AccountCircle, Assignment, Person, Description, VerifiedUser, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StatCard from '../components/StatCard';
import dashboardService from '../services/dashboardService';

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    registered_users_count: 0,
    subscriber_users_count: 0,
    life_declaration_pending_count: 0,
    demise_confirmed_count: 0,
    demise_unconfirmed_count: 0,
    death_certificate_received_count: 0,
    death_certificate_pending_count: 0,
    dc_status_valid_count: 0,
    dc_status_invalid_count: 0,
    dc_status_pending_count: 0,
  });

  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (hasFetched.current) return;
      hasFetched.current = true;
      setLoading(true);
      setError('');
      const result = await dashboardService.getStatistics();
      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.error);
      }
      setLoading(false);
    };
    fetchStatistics();
  }, []);

  const handleSignOut = async () => { await logout(); navigate('/signin'); };

  return (
    <Box sx={{
      flexGrow: 1,
      backgroundColor: '#F8FAFC',
      // Use 100dvh (dynamic viewport height) — adjusts for mobile browser toolbars
      // xs/sm: minus the 64px mobile AppBar
      // md+: full viewport, no AppBar
      height: { xs: 'calc(100dvh - 64px)', md: '100vh' },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      padding: { xs: 1, sm: 1.5, md: 2 },
      // Very small screens (e.g. iPhone SE 1st gen, < 580px height): allow scroll
      '@media (max-height: 580px)': {
        height: 'auto',
        overflow: 'auto',
      },
    }}>

      {/* ── Header ── */}
      <Paper elevation={0} sx={{
        padding: { xs: '10px 14px', sm: '12px 16px', md: '14px 20px' },
        marginBottom: { xs: '8px', sm: '10px', md: '12px' },
        borderRadius: 2,
        background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        color: 'white',
        border: '1px solid #ECF0F1',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '15px', sm: '17px', md: '20px' }, lineHeight: 1.2 }}>
              Dashboard
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: { xs: '11px', sm: '12px', md: '14px' }, mt: 0.3 }}>
              Welcome back! Here's what's happening today.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'center' }}>
            {!isMobile && (
              <>
                <Box component="span" sx={{
                  display: 'inline-flex', alignItems: 'center',
                  backgroundColor: 'rgba(255,255,255,0.2)', color: 'white',
                  fontSize: { sm: '10px', md: '12px' }, padding: { sm: '6px 10px', md: '8px 16px' },
                  borderRadius: '6px', backdropFilter: 'blur(10px)', fontWeight: 600,
                  border: '1px solid rgba(255,255,255,0.3)',
                }}>
                  REGISTERED USER - {loading ? '...' : stats.registered_users_count}
                </Box>
                <Box component="span" sx={{
                  display: 'inline-flex', alignItems: 'center',
                  backgroundColor: 'rgba(255,255,255,0.2)', color: 'white',
                  fontSize: { sm: '10px', md: '12px' }, padding: { sm: '6px 10px', md: '8px 16px' },
                  borderRadius: '6px', backdropFilter: 'blur(10px)', fontWeight: 600,
                  border: '1px solid rgba(255,255,255,0.3)',
                }}>
                  SUBSCRIBER - {loading ? '...' : stats.subscriber_users_count}
                </Box>
              </>
            )}
            <IconButton sx={{ padding: 0, pointerEvents: 'none' }}>
              <Avatar sx={{
                width: { xs: 34, sm: 38, md: 44 }, height: { xs: 34, sm: 38, md: 44 },
                backgroundColor: 'white', border: '2px solid rgba(255,255,255,0.3)',
                boxShadow: '0 4px 14px 0 rgba(0,0,0,0.15)', pointerEvents: 'none',
              }}>
                <AccountCircle sx={{ fontSize: { xs: 24, sm: 26, md: 32 }, color: '#4E83BD' }} />
              </Avatar>
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* ── Mobile: Registered / Subscriber badges ── */}
      {isMobile && (
        <Box sx={{ display: 'flex', gap: 1, mb: '8px', flexShrink: 0 }}>
          <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#4E83BD', color: 'white', fontSize: '10px',
            padding: '5px 10px', borderRadius: '6px', fontWeight: 600, flex: 1,
          }}>
            REGISTERED USER - {loading ? '...' : stats.registered_users_count}
          </Box>
          <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#4E83BD', color: 'white', fontSize: '10px',
            padding: '5px 10px', borderRadius: '6px', fontWeight: 600, flex: 1,
          }}>
            SUBSCRIBER - {loading ? '...' : stats.subscriber_users_count}
          </Box>
        </Box>
      )}

      {/* ── Loading ── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* ── Error ── */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>{error}</Alert>
      )}

      {/* ── Stats Tree ── */}
      {!loading && !error && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* ── Level 1: Life Declaration label + card ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: '5px', flexShrink: 0 }}>
            <Assignment sx={{ color: '#4E83BD', fontSize: { xs: 15, md: 18 } }} />
            <Typography sx={{ color: '#1E293B', fontSize: { xs: '11px', md: '13px' }, fontWeight: 700 }}>
              Life Declaration
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <StatCard label="Pending" value={stats.life_declaration_pending_count.toString()} icon={<Assignment />} color="#4E83BD" />
          </Box>

          {/* ── Connector + Demise label (lines run top-to-bottom of this 44px band) ── */}
          <Box sx={{ position: 'relative', height: '44px', flexShrink: 0 }}>
            <svg width="100%" height="44" style={{ position: 'absolute', top: 0, left: 0 }}>
              {/* Drop from Life Declaration center (50%) */}
              <line x1="50%" y1="0"  x2="50%" y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Horizontal spread: confirmed (25%) → unconfirmed (75%) */}
              <line x1="25%" y1="22" x2="75%" y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Drops into Demise cards */}
              <line x1="25%" y1="22" x2="25%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="75%" y1="22" x2="75%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {/* Section label overlaid on the left of the connector band */}
            <Box sx={{
              position: 'absolute', top: '50%', left: 0,
              transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: 0.5,
              backgroundColor: '#F8FAFC', pr: 1, zIndex: 1,
            }}>
              <Person sx={{ color: '#3498DB', fontSize: { xs: 14, md: 16 } }} />
              <Typography sx={{ color: '#1E293B', fontSize: { xs: '11px', md: '13px' }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Demise
              </Typography>
            </Box>
          </Box>

          {/* ── Level 2: Demise cards ── */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: { xs: '8px', md: '12px' } }}>
            <StatCard label="Confirmed"   value={stats.demise_confirmed_count.toString()}   icon={<Person />} color="#3498DB" />
            <StatCard label="Unconfirmed" value={stats.demise_unconfirmed_count.toString()} icon={<Person />} color="#E67E22" />
          </Box>

          {/* ── Connector + Death Certificate label ── */}
          <Box sx={{ position: 'relative', height: '44px', flexShrink: 0 }}>
            <svg width="100%" height="44" style={{ position: 'absolute', top: 0, left: 0 }}>
              {/* Drop from Confirmed card center (left card of 2 = 25%) */}
              <line x1="25%" y1="0"  x2="25%" y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Horizontal spread: received (25%) → dc-pending (75%) */}
              <line x1="25%" y1="22" x2="75%" y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Drops into Death Certificate cards */}
              <line x1="25%" y1="22" x2="25%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="75%" y1="22" x2="75%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <Box sx={{
              position: 'absolute', top: '50%', left: 0,
              transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: 0.5,
              backgroundColor: '#F8FAFC', pr: 1, zIndex: 1,
            }}>
              <Description sx={{ color: '#4E83BD', fontSize: { xs: 14, md: 16 } }} />
              <Typography sx={{ color: '#1E293B', fontSize: { xs: '11px', md: '13px' }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Death Certificate
              </Typography>
            </Box>
          </Box>

          {/* ── Level 3: Death Certificate cards ── */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: { xs: '8px', md: '12px' } }}>
            <StatCard label="Received" value={stats.death_certificate_received_count.toString()} icon={<Description />} color="#3498DB" />
            <StatCard label="Pending"  value={stats.death_certificate_pending_count.toString()}  icon={<Description />} color="#E67E22" />
          </Box>

          {/* ── Connector + DC Status label ── */}
          <Box sx={{ position: 'relative', height: '44px', flexShrink: 0 }}>
            <svg width="100%" height="44" style={{ position: 'absolute', top: 0, left: 0 }}>
              {/* Drop from Received card center (left card of 2 = 25%) */}
              <line x1="25%"    y1="0"  x2="25%"    y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Horizontal spread across all three DC Status cards */}
              <line x1="16.67%" y1="22" x2="83.33%" y2="22" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              {/* Drops into DC Status cards (valid=16.67%, invalid=50%, pending=83.33%) */}
              <line x1="16.67%" y1="22" x2="16.67%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="50%"    y1="22" x2="50%"    y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="83.33%" y1="22" x2="83.33%" y2="44" stroke="#93C5E8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <Box sx={{
              position: 'absolute', top: '50%', left: 0,
              transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: 0.5,
              backgroundColor: '#F8FAFC', pr: 1, zIndex: 1,
            }}>
              <VerifiedUser sx={{ color: '#2ECC71', fontSize: { xs: 14, md: 16 } }} />
              <Typography sx={{ color: '#1E293B', fontSize: { xs: '11px', md: '13px' }, fontWeight: 700, whiteSpace: 'nowrap' }}>
                DC Status
              </Typography>
            </Box>
          </Box>

          {/* ── Level 4: DC Status cards ── */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: { xs: '8px', md: '12px' } }}>
            <StatCard label="Valid"   value={stats.dc_status_valid_count.toString()}   icon={<VerifiedUser />} color="#2ECC71" />
            <StatCard label="Invalid" value={stats.dc_status_invalid_count.toString()} icon={<VerifiedUser />} color="#E74C3C" />
            <StatCard label="Pending" value={stats.dc_status_pending_count.toString()} icon={<VerifiedUser />} color="#E67E22" />
          </Box>

        </Box>
      )}

      {/* ── Sign Out Dialog ── */}
      <Dialog open={signOutDialogOpen} onClose={() => setSignOutDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2, padding: 1 } }}>
        <DialogContent sx={{ padding: 4, textAlign: 'center' }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: '50%', backgroundColor: '#FEE2E2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
          }}>
            <Logout sx={{ fontSize: 40, color: '#DC2626' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '20px', color: '#2C3E50', mb: 1 }}>
            Sign Out
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6B7280', lineHeight: 1.6, mb: 3 }}>
            Are you sure you want to sign out from your account?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={() => setSignOutDialogOpen(false)} sx={{
              textTransform: 'none', fontSize: '14px', color: '#6B7280', borderColor: '#D1D5DB',
              px: 3, py: 1, borderRadius: '8px', fontWeight: 600,
              '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
            }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSignOut} sx={{
              textTransform: 'none', fontSize: '14px', backgroundColor: '#DC2626',
              color: 'white', px: 3, py: 1, borderRadius: '8px', fontWeight: 600,
              '&:hover': { backgroundColor: '#B91C1C' },
            }}>
              Sign Out
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Dashboard;